import { styled, Switch, Tooltip } from '@mui/material'
import * as S from './SwitchMui.styles'
import { CSSProperties } from 'styled-components'

interface SwitchThumbProps {
  width: number
  height: number
  activeWidth: number
  borderRadius: number
}

interface SwitchTrackProps {
  borderRadius: number
}

interface CustomSwitchProps {
  width: number
  height: number
  thumb: SwitchThumbProps
  track: SwitchTrackProps
}

const switchSizeAttributes: Record<string, { fontSize: string, switchProps: CustomSwitchProps }> = {
  small: {
    fontSize: '12px',
    switchProps: {
      width: 26,
      height: 14,
      thumb: {
        width: 10,
        height: 10,
        activeWidth: 13,
        borderRadius: 6,
      },
      track: {
        borderRadius: 8,
      },
    },
  },
  medium: {
    fontSize: '14px',
    switchProps: {
      width: 28,
      height: 16,
      thumb: {
        width: 12,
        height: 12,
        activeWidth: 15,
        borderRadius: 6,
      },
      track: {
        borderRadius: 8,
      },
    },
  },
  large: {
    fontSize: '16px',
    switchProps: {
      width: 30,
      height: 18,
      thumb: {
        width: 14,
        height: 14,
        activeWidth: 17,
        borderRadius: 6,
      },
      track: {
        borderRadius: 8,
      },
    },
  },
  big: {
    fontSize: '20px',
    switchProps: {
      width: 34,
      height: 22,
      thumb: {
        width: 18,
        height: 18,
        activeWidth: 21,
        borderRadius: 10,
      },
      track: {
        borderRadius: 12,
      },
    },
  },
}

const CustomSwitch = styled(Switch)<CustomSwitchProps>(({
  width = 28,
  height = 16,
  thumb = {
    width: 12,
    height: 12,
    activeWidth: 15,
    borderRadius: 6,
  },
  track = {
    borderRadius: 8,
  },
}) => ({
  width,
  height,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: thumb.activeWidth,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#1890ff',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: thumb.width,
    height: thumb.height,
    borderRadius: thumb.borderRadius,
    transition: 'width 200ms',
  },
  '& .MuiSwitch-track': {
    borderRadius: track.borderRadius,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}))

type tooltipPlacement =
  | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top'

type justifyContentType =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'

type sizeType = 'small' | 'medium' | 'large' | 'big'

interface SwitchMuiProps {
  checked: boolean
  onClick: () => void
  testId?: string
  offText?: string
  onText?: string
  offTextTooltip?: string
  offTextTooltipPlacement?: tooltipPlacement
  onTextTooltip?: string
  onTextTooltipPlacement?: tooltipPlacement
  width?: string
  fullWidth?: boolean
  justifyContent?: justifyContentType
  size?: sizeType
  style?: CSSProperties
}

const SwitchMui = ({
  checked,
  onClick,
  testId = 'switch-mui-test-id',
  offText = 'off',
  offTextTooltip = '',
  offTextTooltipPlacement = 'left',
  onText = 'on',
  onTextTooltip = '',
  onTextTooltipPlacement = 'right',
  width = 'fit-content',
  fullWidth = false,
  justifyContent = 'center',
  size = 'medium',
  style = {},
}: SwitchMuiProps) => {
  const { fontSize, switchProps } = switchSizeAttributes[size]

  return (
    <S.Wrapper
      width={fullWidth ? '100%' : width}
      justifyContent={justifyContent}
      style={{ ...style }}
    >
      <Tooltip
        title={offTextTooltip}
        placement={offTextTooltipPlacement}
        arrow
      >
        <S.Text fontSize={fontSize}>
          {offText}
        </S.Text>
      </Tooltip>
      <CustomSwitch
        checked={checked}
        onClick={onClick}
        data-testid={testId}
        {...switchProps}
      />
      <Tooltip
        title={onTextTooltip}
        placement={onTextTooltipPlacement}
        arrow
      >
        <S.Text fontSize={fontSize}>
          {onText}
        </S.Text>
      </Tooltip>
    </S.Wrapper>
  )
}

export default SwitchMui
